import React from 'react';

import { Box, Flex, useTheme } from '@chakra-ui/react';
import styled from '@emotion/styled';
import MeshIconButton from 'components/RabbitOKRCreationPanel/components/MeshIconButton';
import TextWithEllipsis from 'components/TextWithEllipsis';
import { useViewPageName } from 'components/Views/Hooks/useViewPageName';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useSelector, useDispatch } from 'react-redux';
import { kRDrawerSelector } from 'selectors/kRDrawer';
import { kRDrawerSecondaryPanelSelector } from 'selectors/krSecondaryPanel';
import { checkGoalGenAIEnabled } from 'utils/helpers/featureFlags';

import { Checkin, CollapseSidepanel, Plus } from 'assets/assetsV1/iconComponents';

import { OKR_TYPES } from 'constants/ProjectConstants';
import { isMobileView } from 'constants/isMobileView';
import {
  sendClickedOnCreateKRevent,
  sendClickedOnCreateObjectiveEvent,
  sendCreatedKREvent,
  sendCreatedObjectiveEvent,
  sendOpenedObjectivePanelEvent,
  sendOpenedKRPanelEvent,
  sendPageViewEvent,
} from 'constants/mixpanelHelper';

import { Button } from 'uiToolkitV1/Button';
import { SplitButton } from 'uiToolkitV1/SplitButton';
import { TextBodyMedium, TextHeader4, TextLabel } from 'uiToolkitV1/Typography';

import { useOKRAccessControl } from '../AccessControl/useOKRAccessControl';
import { getOKRIcon } from '../OKRSidePanel';
import { openCreateOKRPanel } from '../krDrawerSecondaryPanelSlice';

const StyledCollapseSidepanel = styled(CollapseSidepanel)`
  color: #666666 !important;
`;

export const SectionHeader = ({ children, headerText = '', textProps, ...props }) => {
  const theme = useTheme();
  return (
    <Flex h="40px" alignItems="center" justifyContent="space-between" py={1} px={3} {...props}>
      <TextHeader4 bold color={theme.colors.text.g1} fontSize="14px" lineHeight="20px" {...textProps}>
        {headerText}
      </TextHeader4>
      {children}
    </Flex>
  );
};

export const OKRSidePanelSectionHeader = ({ children, headerText = '', textProps, ...props }) => {
  const theme = useTheme();
  return (
    <Flex alignItems="center" justifyContent="space-between" padding="0px 12px 8px 12px;" gap="12px" {...props}>
      <Box display="flex" padding="8px 0px" alignItems="center" flex="1 0 0">
        <TextHeader4 bold color={theme.colors.text.g1} {...textProps}>
          {headerText}
        </TextHeader4>
      </Box>
      {children}
    </Flex>
  );
};

export const OKRSidePanelSecondaryPanelHeader = ({ children, headerText = '', textProps, ...props }) => {
  const theme = useTheme();
  return (
    <Flex alignItems="center" justifyContent="space-between" padding="0px 12px 4px 12px;" gap="12px" {...props}>
      <Box display="flex" padding="8px 0px" alignItems="center" flex="1 0 0">
        <TextHeader4 bold color={theme.colors.text.g1} {...textProps}>
          {headerText}
        </TextHeader4>
      </Box>
      {children}
    </Flex>
  );
};

const OverviewSectionHeader = ({ onCheckinClick, canUpdateOKRStatusAndProgress = false }) => {
  const { type } = useSelector(kRDrawerSecondaryPanelSelector);
  const isSecondaryPanelOpen = Boolean(type);

  return (
    <SectionHeader headerText="Overview">
      {canUpdateOKRStatusAndProgress ? (
        <Button
          StartIcon={Checkin}
          variant="filled"
          style={{ height: '32px' }}
          onClick={onCheckinClick}
          disabled={isSecondaryPanelOpen}
        >
          Check-in
        </Button>
      ) : null}
    </SectionHeader>
  );
};

const CheckInSectionHeader = () => <OKRSidePanelSectionHeader headerText="Check-in" />;

const ChangeDetailsPanelHeader = ({ onCollapse }) => (
  <OKRSidePanelSecondaryPanelHeader headerText="Change Details">
    {' '}
    <Button StartIcon={StyledCollapseSidepanel} variant="tertiary" style={{ height: '32px' }} onClick={onCollapse} />
  </OKRSidePanelSecondaryPanelHeader>
);
const HistorySectionHeader = ({ headerText = 'History', warningTextComp = null, ...props }) => {
  const theme = useTheme();
  return (
    <Box display="flex" flexDirection="column">
      {warningTextComp}
      <Flex alignItems="center" justifyContent="space-between" padding="0px 12px 8px 12px;" gap="12px" {...props}>
        <Box display="flex" padding="8px 0px" alignItems="center" flex="1 0 0">
          <TextHeader4 bold color={theme.colors.text.g1}>
            {headerText}
          </TextHeader4>
        </Box>
      </Flex>
    </Box>
  );
};

const FullHistoryPanelHeader = ({ onCollapse, goalCreatedByMessage }) => {
  const theme = useTheme();
  const headerText = 'History';
  return (
    <Flex alignItems="center" justifyContent="space-between" padding="0px 12px 4px 12px;" gap="12px">
      <Box display="flex" padding="8px 0px" alignItems="center" flex="1 0 0">
        <Box display="flex" flexDir="column">
          <TextHeader4 bold color={theme.colors.text.g1}>
            {headerText}
          </TextHeader4>
          <TextLabel color="#666">{goalCreatedByMessage}</TextLabel>
        </Box>
      </Box>
      <Button StartIcon={StyledCollapseSidepanel} variant="tertiary" style={{ height: '32px' }} onClick={onCollapse} />
    </Flex>
  );
};

const DetailsSectionHeader = ({ handleClickOnChangeDetails, canEditOKR }) => (
  <OKRSidePanelSectionHeader headerText="Details">
    {canEditOKR ? (
      <Button
        variant="outlined"
        style={{ height: '32px' }}
        onClick={() => {
          handleClickOnChangeDetails();
        }}
      >
        Change Details
      </Button>
    ) : null}
  </OKRSidePanelSectionHeader>
);

const MeasureSectionHeader = ({ isDisabled, disabledReason, onButtonClick = () => {} }) => (
  <SectionHeader headerText="Tracking">
    {checkGoalGenAIEnabled() && (
      <MeshIconButton
        isDisabled={isDisabled}
        tooltipText="Setup with Maven"
        disabledReason={disabledReason}
        onClick={() => {
          onButtonClick();
        }}
      />
    )}
  </SectionHeader>
);

const SupportedBySectionHeader = ({ isEmpty }) => {
  const dispatch = useDispatch();
  const page = useViewPageName();
  const { uuid, type, text, visibilityType, timeBoundDetails, progressType, measure, measureType } =
    useSelector(kRDrawerSelector);
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const { canCreateKeyResult, canCreateObjective } = useOKRAccessControl();
  const isObjective = type === OKR_TYPES.OBJECTIVE;

  const handleAddChild = (okrPanelType) => {
    const payload = {
      type: okrPanelType,
      parentPost: {
        uuid,
        type,
        text,
        visibilityType,
      },
      timeBoundDetails,
      parentProgressType: progressType,
      parentMeasure: measure,
      parentMeasureType: measureType,
      onKeyResultCreation(okrDetails) {
        sendCreatedKREvent({
          eventProperties: {
            source: isObjective ? 'Edit Objective Panel' : 'Edit KR Panel',
            page,
            goalId: okrDetails?.uuid,
          },
        });
      },
      onObjectiveCreation(okrDetails) {
        sendCreatedObjectiveEvent({
          eventProperties: {
            source: isObjective ? 'Edit Objective Panel' : 'Edit KR Panel',
            page,
            objectiveId: okrDetails?.uuid,
            description: okrDetails?.description,
            level: okrDetails?.objectiveLevel,
            type: okrDetails?.type,
            timePeriod: okrDetails?.timeBoundDetails?.timelineTag,
          },
        });
      },
    };
    dispatch(openCreateOKRPanel(payload));
  };

  const objectionOption = {
    value: OKR_TYPES.OBJECTIVE,
    label: `Add Child ${objectivesName}`,
    onClick: () => {
      handleAddChild(OKR_TYPES.OBJECTIVE);
      sendClickedOnCreateObjectiveEvent({
        eventProperties: { source: isObjective ? 'Edit Objective Panel' : 'Edit KR Panel', page },
      });
      sendOpenedObjectivePanelEvent({
        eventProperties: {
          source: isObjective ? 'Edit Objective Panel' : 'Edit KR Panel',
          page,
          type: 'Add Objective Panel',
        },
      });
      sendPageViewEvent({
        eventProperties: {
          page: 'Add Objective Panel',
          type: 'Panel',
          source: isObjective ? 'Edit Objective Panel' : 'Edit KR Panel',
        },
      });
    },
  };

  const keyResultOption = {
    value: OKR_TYPES.KEY_RESULT,
    label: `Add Child ${keyResultsName}`,
    onClick: () => {
      handleAddChild(OKR_TYPES.KEY_RESULT);
      sendClickedOnCreateKRevent({
        eventProperties: { source: isObjective ? 'Edit Objective Panel' : 'Edit KR Panel', page },
      });
      sendOpenedKRPanelEvent({
        eventProperties: {
          source: isObjective ? 'Edit Objective Panel' : 'Edit KR Panel',
          page,
          type: 'Add KR Panel',
        },
      });
      sendPageViewEvent({
        eventProperties: {
          source: isObjective ? 'Edit Objective Panel' : 'Edit KR Panel',
          page: 'Add KR Panel',
          type: 'Panel',
        },
      });
    },
  };

  const options = [];

  if (canCreateKeyResult) options.push(keyResultOption);
  if (canCreateObjective) options.push(objectionOption);

  return (
    <SectionHeader headerText={`Child ${moduleName} Alignment`} height="40px">
      {!isEmpty && (
        <>
          {options.length === 1 ? (
            <Button variant="outlined" onClick={options[0]?.onClick} style={{ height: '32px' }}>
              {options[0]?.label}
            </Button>
          ) : null}
          {options.length > 1 ? (
            <SplitButton
              buttonProps={{
                variant: 'outlined',
                withBackground: false,
                style: { padding: '8px', borderRadius: '4px 0px 0px 4px', height: isMobileView() ? '40px' : undefined },
              }}
              menuButtonProps={{
                variant: 'outlined',
                withBackground: false,
                style: { padding: '8px', borderRadius: '0px 4px 4px 0px', height: isMobileView() ? '40px' : undefined },
              }}
              options={options}
              StartIcon={isMobileView() ? null : Plus}
            />
          ) : null}
        </>
      )}
    </SectionHeader>
  );
};

const RealignPanelHeader = ({ headerText = 'Re-align', onCollapse, ...rest }) => {
  const { okrType, okrText, okrVisibilityType } = rest;

  const isObjectiveRealignment = okrType === OKR_TYPES.OBJECTIVE;

  const OKRIcon = getOKRIcon(isObjectiveRealignment, okrVisibilityType);

  return (
    <SectionHeader headerText={headerText} flexWrap="wrap" h="70px">
      <Button StartIcon={StyledCollapseSidepanel} variant="tertiary" style={{ height: '32px' }} onClick={onCollapse} />
      <Flex flex="100% 0 0" alignItems="flex-end">
        <OKRIcon />
        <TextWithEllipsis tooltipText={okrText} linesToShow={1}>
          <TextBodyMedium className="text-with-ellipsis-tooltip" ml={2}>
            {' '}
            {okrText}{' '}
          </TextBodyMedium>
        </TextWithEllipsis>
      </Flex>
    </SectionHeader>
  );
};

const SecondaryPanelHeader = ({ headerText = '', onCollapse }) => (
  <SectionHeader headerText={headerText}>
    <Button StartIcon={StyledCollapseSidepanel} variant="tertiary" style={{ height: '32px' }} onClick={onCollapse} />
  </SectionHeader>
);

export {
  OverviewSectionHeader,
  DetailsSectionHeader,
  MeasureSectionHeader,
  RealignPanelHeader,
  SupportedBySectionHeader,
  SecondaryPanelHeader,
  CheckInSectionHeader,
  ChangeDetailsPanelHeader,
  HistorySectionHeader,
  FullHistoryPanelHeader,
};
